import React from "react";
import { AlertCircle, X } from "lucide-react";
import { useBranding } from "@/lib/branding";

/**
 * DevAdvisoryBanner
 *
 * Slim, polite advisory bar surfaced at the very top of the app whenever
 * `dev_banner_enabled` is on in Settings. Guests can dismiss the banner
 * for their current browser (persisted per-message-hash in localStorage
 * so a fresh operator message re-shows even after a previous dismiss).
 *
 * Rendered globally by App.js so every route (landing, guest menu, iPad
 * cabin view, crew, admin) picks it up without needing per-page wiring.
 */
export default function DevAdvisoryBanner() {
  const { devBannerEnabled, devBannerText } = useBranding();
  const msg = (devBannerText || "").trim();

  // Hash the message so a NEW operator note re-shows even after a
  // previous dismiss — a subtle behaviour that avoids stale silences.
  const key = React.useMemo(() => {
    let h = 5381;
    for (let i = 0; i < msg.length; i++) h = ((h << 5) + h + msg.charCodeAt(i)) | 0;
    return `yspa:dev-banner:${h}`;
  }, [msg]);

  const [dismissed, setDismissed] = React.useState(() => {
    try { return localStorage.getItem(key) === "1"; } catch { return false; }
  });

  if (!devBannerEnabled || !msg || dismissed) return null;

  return (
    <div
      className="w-full bg-amber-500/10 border-b border-amber-500/30 text-amber-900 dark:text-amber-200"
      data-testid="dev-advisory-banner"
      role="status"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex items-center gap-3">
        <AlertCircle className="h-4 w-4 shrink-0 opacity-80" strokeWidth={1.8} />
        <p className="text-[13px] leading-snug flex-1" data-testid="dev-advisory-text">
          {msg}
        </p>
        <button
          type="button"
          onClick={() => {
            try { localStorage.setItem(key, "1"); } catch { /* ignore */ }
            setDismissed(true);
          }}
          className="opacity-60 hover:opacity-100 transition-opacity p-1 -m-1"
          aria-label="Dismiss advisory"
          data-testid="dev-advisory-dismiss"
        >
          <X className="h-3.5 w-3.5" strokeWidth={1.8} />
        </button>
      </div>
    </div>
  );
}
